import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

// Smart API base: If the app is served from a different domain than REACT_APP_BACKEND_URL,
// use relative "/api" so requests go to the same domain (works with any custom domain).
// This handles custom domain deployments (e.g., tcrm.hka.ae) where the
// build-time BACKEND_URL doesn't match the serving domain.
function resolveApiBase() {
  if (!BACKEND_URL) return '/api';
  try {
    const backendHost = new URL(BACKEND_URL).hostname;
    const currentHost = typeof window !== 'undefined' ? window.location.hostname : '';
    // If running on a different domain than what was baked in at build time, use relative URL
    if (currentHost && currentHost !== backendHost) {
      return '/api';
    }
  } catch {
    // URL parsing failed — fall back to relative
    return '/api';
  }
  return `${BACKEND_URL}/api`;
}

export const API_BASE = resolveApiBase();

// In-memory token storage (NOT localStorage — secure, lives only in RAM)
// Used as fallback when httpOnly cookies don't work across proxy layers
let _authToken = null;

export const setAuthToken = (token) => {
  _authToken = token;
};

export const getAuthHeaders = () => {
  return _authToken ? { Authorization: `Bearer ${_authToken}` } : {};
};

// Create axios instance
const api = axios.create({
  baseURL: API_BASE,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true,  // Send httpOnly cookies automatically
});

// Add in-memory auth token to requests as fallback
api.interceptors.request.use((config) => {
  if (_authToken) {
    config.headers.Authorization = `Bearer ${_authToken}`;
  }
  return config;
});

// Handle auth errors — redirect to login on 401
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Avoid redirect loops on the login or auth/me endpoints
      const url = error.config?.url || '';
      if (!url.includes('/auth/login') && !url.includes('/auth/me')) {
        _authToken = null;
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

export default api;

// Auth
export const login = (email, password) => api.post('/auth/login', { email, password });
export const logout = () => api.post('/auth/logout');
export const getMe = () => api.get('/auth/me');

// Dashboard
export const getDashboardStats = () => api.get('/dashboard/stats');
export const getExpiringDocuments = (days = 30, limit = 10) => api.get(`/dashboard/expiring-documents?days=${days}&limit=${limit}`);
export const getRecentInvoices = (limit = 5) => api.get(`/dashboard/recent-invoices?limit=${limit}`);
export const getOverdueInvoices = (limit = 5) => api.get(`/dashboard/overdue-invoices?limit=${limit}`);

// Clients
export const getClients = (params = {}) => api.get('/clients', { params });
export const getClient = (id) => api.get(`/clients/${id}`);
export const createClient = (data) => api.post('/clients', data);
export const updateClient = (id, data) => api.put(`/clients/${id}`, data);
export const deleteClient = (id) => api.delete(`/clients/${id}`);

// Wallet
export const getClientWallet = (clientId) => api.get(`/clients/${clientId}/wallet`);
export const getWalletTransactions = (clientId, params = {}) => api.get(`/clients/${clientId}/wallet/transactions`, { params });
export const topupWallet = (clientId, data) => api.post(`/clients/${clientId}/wallet/topup`, data);
export const refundToWallet = (clientId, data) => api.post(`/clients/${clientId}/wallet/refund`, data);
export const payFromWallet = (clientId, data) => api.post(`/clients/${clientId}/wallet/pay`, data);
export const getLowBalanceAlerts = () => api.get('/wallet/low-balance-alerts');
export const getWalletDashboardStats = () => api.get('/dashboard/wallet-stats');

// Workers
export const getWorkers = (params = {}) => api.get('/workers', { params });
export const getWorker = (id) => api.get(`/workers/${id}`);
export const createWorker = (data) => api.post('/workers', data);
export const updateWorker = (id, data) => api.put(`/workers/${id}`, data);
export const deleteWorker = (id) => api.delete(`/workers/${id}`);

// Services
export const getServiceCategories = () => api.get('/service-categories');
export const createServiceCategory = (data) => api.post('/service-categories', data);
export const updateServiceCategory = (id, data) => api.put(`/service-categories/${id}`, data);
export const deleteServiceCategory = (id) => api.delete(`/service-categories/${id}`);
export const getServices = (params = {}) => api.get('/services', { params });
export const getService = (id) => api.get(`/services/${id}`);
export const createService = (data) => api.post('/services', data);
export const updateService = (id, data) => api.put(`/services/${id}`, data);
export const deleteService = (id) => api.delete(`/services/${id}`);

// Service Orders
export const getServiceOrders = (params = {}) => api.get('/service-orders', { params });
export const getServiceOrdersKanban = () => api.get('/service-orders/kanban');
export const getServiceOrder = (id) => api.get(`/service-orders/${id}`);
export const createServiceOrder = (data) => api.post('/service-orders', data);
export const updateServiceOrder = (id, data) => api.put(`/service-orders/${id}`, data);
export const updateServiceOrderItem = (orderId, itemId, data) => api.put(`/service-orders/${orderId}/items/${itemId}`, data);
export const deleteServiceOrder = (id) => api.delete(`/service-orders/${id}`);
export const convertOrderToInvoice = (id) => api.post(`/service-orders/${id}/convert-to-invoice`);

// Service Item Workers
export const getServiceItemWorkers = (orderId, itemId) => api.get(`/service-orders/${orderId}/items/${itemId}/workers`);
export const updateServiceItemWorkers = (orderId, itemId, data) => api.put(`/service-orders/${orderId}/items/${itemId}/workers`, data);

// Document Types
export const getDocumentTypes = () => api.get('/document-types');
export const createDocumentType = (data) => api.post('/document-types', data);
export const updateDocumentType = (id, data) => api.put(`/document-types/${id}`, data);
export const deleteDocumentType = (id) => api.delete(`/document-types/${id}`);

// Documents
export const getDocuments = (params = {}) => api.get('/documents', { params });
export const getDocument = (id) => api.get(`/documents/${id}`);
export const createDocument = (data) => api.post('/documents', data);
export const updateDocument = (id, data) => api.put(`/documents/${id}`, data);
export const deleteDocument = (id) => api.delete(`/documents/${id}`);

// Invoices
export const getInvoices = (params = {}) => api.get('/invoices', { params });
export const getInvoice = (id) => api.get(`/invoices/${id}`);
export const createInvoice = (data) => api.post('/invoices', data);
export const updateInvoice = (id, data) => api.put(`/invoices/${id}`, data);
export const updateInvoiceStatus = (id, status) => api.put(`/invoices/${id}/status?status=${status}`);
export const deleteInvoice = (id) => api.delete(`/invoices/${id}`);
export const getInvoicePdf = (id) => api.get(`/invoices/${id}/pdf`, { responseType: 'blob' });
export const getInvoicePayments = (id) => api.get(`/invoices/${id}/payments`);

// Invoice Documents
export const getInvoiceDocumentTypes = () => api.get('/invoice-document-types');
export const getInvoiceDocuments = (invoiceId) => api.get(`/invoices/${invoiceId}/documents`);
export const addInvoiceDocument = (invoiceId, data) => api.post(`/invoices/${invoiceId}/documents`, data);
export const updateInvoiceDocument = (invoiceId, docId, data) => api.put(`/invoices/${invoiceId}/documents/${docId}`, data);
export const deleteInvoiceDocument = (invoiceId, docId) => api.delete(`/invoices/${invoiceId}/documents/${docId}`);
export const uploadInvoiceDocument = (invoiceId, formData) => api.post(`/invoices/${invoiceId}/documents/upload`, formData, {
  headers: { 'Content-Type': 'multipart/form-data' }
});
export const downloadInvoiceDocument = (invoiceId, docId) => api.get(`/invoices/${invoiceId}/documents/${docId}/download`, { responseType: 'blob' });

// Payments
export const getPayments = (params = {}) => api.get('/payments', { params });
export const createPayment = (data) => api.post('/payments', data);
export const createSplitPayment = (data) => api.post('/payments/split', data);
export const deletePayment = (id) => api.delete(`/payments/${id}`);

// Quotations
export const getQuotations = (params = {}) => api.get('/quotations', { params });
export const getQuotation = (id) => api.get(`/quotations/${id}`);
export const createQuotation = (data) => api.post('/quotations', data);
export const updateQuotationStatus = (id, status) => api.put(`/quotations/${id}/status?status=${status}`);
export const convertQuotationToInvoice = (id) => api.post(`/quotations/${id}/convert-to-invoice`);
export const deleteQuotation = (id) => api.delete(`/quotations/${id}`);

// Credit Notes
export const getCreditNotes = (params = {}) => api.get('/credit-notes', { params });
export const createCreditNote = (data) => api.post('/credit-notes', data);
export const applyCreditNote = (cnId, invoiceId, amount) => api.post(`/credit-notes/${cnId}/apply`, { invoice_id: invoiceId, amount });

// Expenses
export const getExpenseCategories = () => api.get('/expense-categories');
export const createExpenseCategory = (data) => api.post('/expense-categories', data);
export const updateExpenseCategory = (id, data) => api.put(`/expense-categories/${id}`, data);
export const deleteExpenseCategory = (id) => api.delete(`/expense-categories/${id}`);
export const getExpenses = (params = {}) => api.get('/expenses', { params });
export const createExpense = (data) => api.post('/expenses', data);
export const updateExpense = (id, data) => api.put(`/expenses/${id}`, data);
export const approveExpense = (id) => api.put(`/expenses/${id}/approve`);
export const deleteExpense = (id) => api.delete(`/expenses/${id}`);

// Reports
export const getRevenueReport = (params = {}) => api.get('/reports/revenue', { params });
export const getCollectionsReport = (params = {}) => api.get('/reports/collections', { params });
export const getAgingReport = () => api.get('/reports/aging');
export const getDocumentExpiryReport = (params = {}) => api.get('/reports/document-expiry', { params });
export const getVatReport = (params = {}) => api.get('/reports/vat', { params });
export const getServiceOrdersReport = (params = {}) => api.get('/reports/service-orders', { params });

// File Uploads
export const uploadFile = (file, entityType, entityId) => {
  const formData = new FormData();
  formData.append('file', file);
  return api.post(`/files/upload?entity_type=${entityType}&entity_id=${entityId}`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  });
};
export const getEntityFiles = (entityType, entityId) => api.get(`/files/entity/${entityType}/${entityId}`);
export const deleteFile = (fileId) => api.delete(`/files/${fileId}`);

// Reminders
export const getReminders = (params = {}) => api.get('/reminders', { params });

// Settings
export const getSettings = () => api.get('/settings');
export const updateSetting = (key, value) => api.put(`/settings/${key}`, { setting_value: value });

// Custom Fields
export const getCustomFields = (params = {}) => api.get('/custom-fields', { params });
export const createCustomField = (data) => api.post('/custom-fields', data);
export const updateCustomField = (id, data) => api.put(`/custom-fields/${id}`, data);
export const deleteCustomField = (id) => api.delete(`/custom-fields/${id}`);

// Users
export const getUsers = () => api.get('/users');
export const getUser = (id) => api.get(`/users/${id}`);
export const createUser = (data) => api.post('/auth/register', data);
export const updateUser = (id, data) => api.put(`/users/${id}`, data);
export const deleteUser = (id) => api.delete(`/users/${id}`);

// Emirates (Locations)
export const getEmirates = () => api.get('/emirates');
export const createEmirate = (data) => api.post('/emirates', data);
export const updateEmirate = (id, data) => api.put(`/emirates/${id}`, data);
export const deleteEmirate = (id) => api.delete(`/emirates/${id}`);

// Payment Terms
export const getPaymentTerms = () => api.get('/payment-terms');
export const createPaymentTerm = (data) => api.post('/payment-terms', data);
export const updatePaymentTerm = (id, data) => api.put(`/payment-terms/${id}`, data);
export const deletePaymentTerm = (id) => api.delete(`/payment-terms/${id}`);

// Payment Methods
export const getPaymentMethods = () => api.get('/payment-methods');
export const createPaymentMethod = (data) => api.post('/payment-methods', data);
export const updatePaymentMethod = (id, data) => api.put(`/payment-methods/${id}`, data);
export const deletePaymentMethod = (id) => api.delete(`/payment-methods/${id}`);

// Order Statuses
export const getOrderStatuses = () => api.get('/order-statuses');
export const createOrderStatus = (data) => api.post('/order-statuses', data);
export const updateOrderStatus = (id, data) => api.put(`/order-statuses/${id}`, data);
export const deleteOrderStatus = (id) => api.delete(`/order-statuses/${id}`);

// Global Search
export const globalSearch = (query) => api.get('/search', { params: { q: query } });

// Notifications
export const getNotifications = () => api.get('/notifications');
export const markNotificationRead = (id) => api.post(`/notifications/${id}/read`);
export const markAllNotificationsRead = () => api.post('/notifications/read-all');

// Order Status History
export const getOrderStatusHistory = (orderId) => api.get(`/service-orders/${orderId}/history`);
export const updateOrderStatusWithHistory = (orderId, status, notes = '') => 
  api.post(`/service-orders/${orderId}/status`, { status, notes });

// Order Documents / Checklist
export const getOrderDocuments = (orderId) => api.get(`/service-orders/${orderId}/documents`);
export const addOrderDocument = (orderId, data) => api.post(`/service-orders/${orderId}/documents`, data);
export const updateOrderDocument = (orderId, docId, data) => api.put(`/service-orders/${orderId}/documents/${docId}`, data);
export const deleteOrderDocument = (orderId, docId) => api.delete(`/service-orders/${orderId}/documents/${docId}`);

// Payment Allocations
export const getPaymentAllocations = (paymentId) => api.get(`/payments/${paymentId}/allocations`);
export const allocatePayment = (paymentId, invoiceId, amount) => 
  api.post(`/payments/${paymentId}/allocations`, { invoice_id: invoiceId, amount });
export const removePaymentAllocation = (paymentId, allocationId) => 
  api.delete(`/payments/${paymentId}/allocations/${allocationId}`);

// Auto Number Generation
export const getNextNumber = (entityType) => api.get(`/next-number/${entityType}`);
export const incrementNumber = (entityType) => api.post(`/increment-number/${entityType}`);

// Quotation Workflow
export const sendQuotation = (id) => api.post(`/quotations/${id}/send`);
export const acceptQuotation = (id) => api.post(`/quotations/${id}/accept`);
export const rejectQuotation = (id, reason = '') => api.post(`/quotations/${id}/reject`, { reason });
export const convertQuotation = (id) => api.post(`/quotations/${id}/convert-to-invoice`);

// PDF Downloads
export const getQuotationPdf = (id) => api.get(`/quotations/${id}/pdf`, { responseType: 'blob' });
export const getCreditNotePdf = (id) => api.get(`/credit-notes/${id}/pdf`, { responseType: 'blob' });
export const getPaymentReceiptPdf = (id) => api.get(`/payments/${id}/pdf`, { responseType: 'blob' });

// Notification Communications (WhatsApp, SMS, Email)
export const sendNotification = (data) => api.post('/notifications/send', data);
export const sendDirectNotification = (data) => api.post('/notifications/send-direct', data);
export const getNotificationLogs = (params = {}) => api.get('/notifications/logs', { params });
export const getNotificationTemplates = () => api.get('/notifications/templates');
export const getNotificationPreferences = (clientId) => api.get(`/notifications/preferences/${clientId}`);
export const updateNotificationPreferences = (clientId, data) => api.put(`/notifications/preferences/${clientId}`, data);
export const getNotificationSettings = () => api.get('/notifications/settings');
export const testNotification = (channel, templateName = 'WELCOME') => 
  api.post(`/notifications/test?channel=${channel}&template_name=${templateName}`);
export const sendExpiryReminders = (data) => api.post('/notifications/send-expiry-reminders', data);

// Enhanced Expiry Management
export const getExpiryDashboard = () => api.get('/expiry/dashboard');
export const getExpiryList = (params = {}) => api.get('/expiry/list', { params });
export const getExpiryCalendar = (year, month) => api.get('/expiry/calendar', { params: { year, month } });
export const getClientExpirySummary = (clientId) => api.get(`/expiry/by-client/${clientId}`);
export const createOrderFromExpiry = (data) => api.post('/expiry/create-order', data);
export const snoozeExpiryReminder = (expiryId, snoozeDays = 7) => 
  api.post('/expiry/snooze', null, { params: { expiry_id: expiryId, snooze_days: snoozeDays } });
export const getExpiryDocumentTypes = () => api.get('/expiry/document-types');

// Financial Reports
export const getVatStatementReport = (params = {}) => api.get('/reports/financial/vat-statement', { params });
export const getProfitLossReport = (params = {}) => api.get('/reports/financial/profit-loss', { params });
export const getFinancialSummary = (year) => api.get('/reports/financial/summary', { params: { year } });

// Business Intelligence & Analytics
export const getRevenueAnalytics = (params = {}) => api.get('/analytics/revenue', { params });
export const getClientAnalytics = (params = {}) => api.get('/analytics/clients', { params });
export const getOperationsAnalytics = (params = {}) => api.get('/analytics/operations', { params });
export const getForecastAnalytics = (params = {}) => api.get('/analytics/forecast', { params });
export const getWorkerAnalytics = (params = {}) => api.get('/analytics/workers', { params });

// UAE Government Integrations
export const getGovtServices = () => api.get('/govt/services');
export const getGovtDeptServices = (department) => api.get(`/govt/services/${department}`);
export const getServiceDocuments = (serviceCode) => api.get(`/govt/documents/${serviceCode}`);
export const calculateGovtFee = (params) => api.post('/govt/calculate-fee', null, { params });
export const getServiceTimeline = (serviceCode, urgent = false) => api.get(`/govt/timeline/${serviceCode}`, { params: { urgent } });
export const getGovtApplications = (params = {}) => api.get('/govt/applications', { params });
export const getGovtApplication = (id) => api.get(`/govt/applications/${id}`);
export const createGovtApplication = (data) => api.post('/govt/applications', data);
export const updateGovtApplication = (id, data) => api.put(`/govt/applications/${id}`, data);
export const deleteGovtApplication = (id) => api.delete(`/govt/applications/${id}`);
export const getGovtDashboard = () => api.get('/govt/dashboard');
export const mockGovtStatusUpdate = (id, status, reference) => api.post(`/govt/mock-status-update/${id}`, null, { params: { status, reference } });

// Government Expenses Report
export const getGovtExpensesReport = (params) => api.get('/reports/govt-expenses', { params });
export const getGovtExpensesSummary = () => api.get('/reports/govt-expenses/summary');

// Customer Comprehensive Reports
export const getCustomerComprehensiveReport = (params = {}) => api.get('/reports/customer-comprehensive', { params });
export const exportCustomerReportExcel = (params = {}) => api.get('/reports/customer-comprehensive/export/excel', { 
  params,
  responseType: 'blob' 
});
export const exportCustomerReportPdf = (params = {}) => api.get('/reports/customer-comprehensive/export/pdf', { 
  params,
  responseType: 'blob' 
});

// Role-Based Permissions
export const getAllPermissions = () => api.get('/permissions');
export const getRolePermissions = (role) => api.get(`/permissions/${role}`);
export const updateRolePermissions = (role, permissions) => api.put(`/permissions/${role}`, { permissions });
export const resetPermissions = () => api.post('/permissions/reset');

// Service Profitability Report
export const getServiceProfitabilityReport = (params = {}) => api.get('/reports/service-profitability', { params });

// Custom Report Builder
export const getReportDataSources = () => api.get('/report-builder/data-sources');
export const getReportTemplates = () => api.get('/report-builder/templates');
export const getReportTemplate = (id) => api.get(`/report-builder/templates/${id}`);
export const createReportTemplate = (data) => api.post('/report-builder/templates', data);
export const updateReportTemplate = (id, data) => api.put(`/report-builder/templates/${id}`, data);
export const deleteReportTemplate = (id) => api.delete(`/report-builder/templates/${id}`);
export const executeCustomReport = (data) => api.post('/report-builder/execute', data);
export const exportCustomReportExcel = (data) => api.post('/report-builder/export/excel', data, { responseType: 'blob' });

// Bulk Import
export const validateClientImport = (file) => {
  const formData = new FormData();
  formData.append('file', file);
  return api.post('/import/validate/clients', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
};
export const importClients = (data) => api.post('/import/clients', data);
export const validateWorkerImport = (file) => {
  const formData = new FormData();
  formData.append('file', file);
  return api.post('/import/validate/workers', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
};
export const importWorkers = (data) => api.post('/import/workers', data);
export const getImportTemplate = (entityType) => api.get(`/import/templates/${entityType}`, { responseType: 'blob' });
export const getImportSample = (entityType) => api.get(`/import/samples/${entityType}`, { responseType: 'blob' });

// Recurring Invoices
export const getRecurringInvoices = (params = {}) => api.get('/recurring-invoices', { params });
export const getRecurringInvoiceStats = () => api.get('/recurring-invoices/stats');
export const getRecurringInvoice = (id) => api.get(`/recurring-invoices/${id}`);
export const createRecurringInvoice = (data) => api.post('/recurring-invoices', data);
export const updateRecurringInvoice = (id, data) => api.put(`/recurring-invoices/${id}`, data);
export const deleteRecurringInvoice = (id) => api.delete(`/recurring-invoices/${id}`);
export const pauseRecurringInvoice = (id) => api.post(`/recurring-invoices/${id}/pause`);
export const resumeRecurringInvoice = (id) => api.post(`/recurring-invoices/${id}/resume`);
export const generateRecurringNow = (id) => api.post(`/recurring-invoices/${id}/generate-now`);
export const getRecurringInvoiceHistory = (id, params = {}) => api.get(`/recurring-invoices/${id}/history`, { params });

// Payment Reminders
export const getReminderSettings = () => api.get('/payment-reminders/settings');
export const updateReminderSettings = (data) => api.put('/payment-reminders/settings', data);
export const getReminderClients = (params = {}) => api.get('/payment-reminders/clients', { params });
export const getReminderClientOverride = (clientId) => api.get(`/payment-reminders/clients/${clientId}`);
export const updateReminderClientOverride = (clientId, data) => api.put(`/payment-reminders/clients/${clientId}`, data);
export const getReminderLog = (params = {}) => api.get('/payment-reminders/log', { params });
export const getReminderLogStats = () => api.get('/payment-reminders/log/stats');
export const runRemindersNow = () => api.post('/payment-reminders/run-now');


